import apiClient from './client';

export interface RedactionSummary {
  categories: Record<string, number>;
  method: string;
  note?: string;
  out_filename: string;
}

export type WashMode = 'patterns' | 'ai' | 'none';

export interface PrepareResult {
  extracted_text: string;
  pii_detected: Record<string, number>;
  filename: string;
  char_count: number;
}

export interface WashResult {
  washed_text: string;
  summary: Record<string, number> | { ai_wash?: boolean; note?: string };
  wash_mode: WashMode;
}

// Async AI-wash job (huge docs run many minutes; the UI polls for progress).
export interface WashJobStart {
  job_id: string;
  status: 'running';
  wash_mode: 'ai';
  total_chunks: number;
}

export interface WashJobStatus {
  job_id: string;
  status: 'running' | 'complete' | 'cancelled' | 'error';
  wash_mode: 'ai';
  total_chunks: number;
  completed_chunks: number;
  failed_chunks: number;
  last_chunk_duration_s?: number | null;
  washed_text?: string;
  summary?: WashResult['summary'];
  cancelled?: boolean;
  error?: string;
}

export interface LearnProposal {
  name: string;
  category: string;
  pattern: string;
  examples: string[];
  valid: boolean;
  explanation?: string | null;
}

export interface LearnResult {
  proposals: LearnProposal[];
  skipped_invalid: number;
}

export interface RedactResult {
  blob: Blob;
  filename: string;
  summary: RedactionSummary | null;
}

/**
 * Upload a document for PII/signature redaction. Returns the redacted
 * file as a Blob plus the parsed X-Redaction-Summary header (category
 * counts; no PII). The redacted document is marked EDITED.
 */
export async function redactDocument(file: File, fast: boolean = false): Promise<RedactResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('fast', fast ? 'true' : 'false');
  const resp = await apiClient.post('/tools/redact', form, {
    responseType: 'blob',
  });
  const header = resp.headers['x-redaction-summary'] as string | undefined;
  let summary: RedactionSummary | null = null;
  if (header) {
    try {
      summary = JSON.parse(header) as RedactionSummary;
    } catch {
      summary = null;
    }
  }
  const filename = summary?.out_filename || 'document_REDACTED.txt';
  return { blob: resp.data as Blob, filename, summary };
}

/**
 * Data Wash 3-step flow (DIR-042):
 *   1) prepare: extract text from an upload (no wash, no doc).
 *   2) wash:    produce washed text (patterns | ai | none). The AI mode
 *               runs the LOCAL model only; the endpoint 403s for a cloud
 *               private path (document text may contain real PII).
 *   3) build:   generate the clean doc from the washed text (on download /
 *               format change).
 * The doc is generated ONLY in build (on download), not on upload.
 */
export async function dataWashPrepare(
  file: File,
): Promise<PrepareResult> {
  const form = new FormData();
  form.append('file', file);
  try {
    const { data } = await apiClient.post<PrepareResult>(
      '/tools/data-wash/prepare',
      form,
    );
    return data;
  } catch (err: any) {
    const detail = err?.response?.data?.detail;
    if (detail) throw new Error(String(detail));
    throw err instanceof Error ? err : new Error('Prepare request failed.');
  }
}

export async function dataWashWash(
  extractedText: string,
  washMode: WashMode,
): Promise<WashResult> {
  try {
    const { data } = await apiClient.post<WashResult>(
      '/tools/data-wash/wash',
      { extracted_text: extractedText, wash_mode: washMode },
    );
    return data;
  } catch (err: any) {
    const detail = err?.response?.data?.detail;
    if (detail) throw new Error(String(detail));
    throw err instanceof Error ? err : new Error('Wash request failed.');
  }
}

// Start an async AI wash (returns a job_id immediately; poll dataWashWashStatus).
export async function dataWashWashStart(
  extractedText: string,
): Promise<WashJobStart> {
  try {
    const { data } = await apiClient.post<WashJobStart>(
      '/tools/data-wash/wash',
      { extracted_text: extractedText, wash_mode: 'ai' },
      { timeout: 60_000 },
    );
    return data;
  } catch (err: any) {
    const detail = err?.response?.data?.detail;
    if (detail) throw new Error(String(detail));
    throw err instanceof Error ? err : new Error('AI wash start failed.');
  }
}

// Poll an async AI wash job for live chunk progress + final result.
export async function dataWashWashStatus(jobId: string): Promise<WashJobStatus> {
  try {
    const { data } = await apiClient.get<WashJobStatus>(
      `/tools/data-wash/wash/${jobId}/status`,
    );
    return data;
  } catch (err: any) {
    const detail = err?.response?.data?.detail;
    if (detail) throw new Error(String(detail));
    throw err instanceof Error ? err : new Error('Wash status poll failed.');
  }
}

// Cancel a running async AI wash job.
export async function dataWashWashCancel(jobId: string): Promise<void> {
  try {
    await apiClient.post(`/tools/data-wash/wash/${jobId}/cancel`);
  } catch (err: any) {
    const detail = err?.response?.data?.detail;
    if (detail) throw new Error(String(detail));
    throw err instanceof Error ? err : new Error('Wash cancel failed.');
  }
}

export async function dataWashBuild(
  washedText: string,
  outputFormat: 'pdf' | 'docx' | 'txt',
): Promise<RedactResult> {
  let resp;
  try {
    resp = await apiClient.post('/tools/data-wash/build',
      { washed_text: washedText, output_format: outputFormat },
      { responseType: 'blob' },
    );
  } catch (err: any) {
    // Error responses come back as a Blob (because responseType=blob).
    const blob = err?.response?.data as Blob | undefined;
    if (blob && blob instanceof Blob) {
      const txt = await blob.text();
      try {
        const detail = JSON.parse(txt)?.detail;
        if (detail) throw new Error(String(detail));
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message !== txt) throw parseErr;
        throw new Error(txt || 'Data wash build failed.');
      }
    }
    throw err instanceof Error ? err : new Error('Data wash build failed.');
  }
  const header = resp.headers['x-redaction-summary'] as string | undefined;
  let summary: RedactionSummary | null = null;
  if (header) {
    try {
      summary = JSON.parse(header) as RedactionSummary;
    } catch {
      summary = null;
    }
  }
  const cd = (resp.headers['content-disposition'] as string | undefined) || '';
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : 'document_DATAWASHED.pdf';
  return { blob: resp.data as Blob, filename, summary };
}
/**
 * Data Wash "Learn from document": upload a document to the
 * LOCAL on-prem LLM, which inspects the extracted text and proposes NEW
 * PII rules (regex patterns) NOT already covered by the existing rules.
 * LOCAL only -- the endpoint 403s if the private path is a cloud backend
 * (document text may contain real PII; never sent off-box).
 */
export async function dataWashLearn(
  file: File,
  maxProposals?: number,
): Promise<LearnResult> {
  const form = new FormData();
  form.append('file', file);
  if (maxProposals != null) form.append('max_proposals', String(maxProposals));
  try {
    const { data } = await apiClient.post<LearnResult>(
      '/tools/data-wash/learn',
      form,
    );
    return data;
  } catch (err: any) {
    const detail = err?.response?.data?.detail;
    if (detail) throw new Error(String(detail));
    throw err instanceof Error ? err : new Error('Learn request failed.');
  }
}
